import { useState, useEffect, useRef } from 'react'
import DocumentMiniature from './DocumentMiniature'
import '../styles/design-system.css'
import '../styles/DocumentCapture.css'

const DocumentCapture = ({ documentType, onCapture, onSkip }) => {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const fileInputRef = useRef(null)
  const [capturedImage, setCapturedImage] = useState(null)
  const [instruction, setInstruction] = useState('Aguarde...')
  const [showPreview, setShowPreview] = useState(false)
  const [cameraError, setCameraError] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [facingMode, setFacingMode] = useState('environment') // 'environment' = câmera traseira
  const [captureMode, setCaptureMode] = useState('upload') // 'upload' | 'camera'
  const [shouldMirror, setShouldMirror] = useState(false) // Only mirror on mobile front camera

  console.log('[DOC CAPTURE] Component render, documentType:', documentType, 'shouldMirror:', shouldMirror, 'facingMode:', facingMode)

  useEffect(() => {
    console.log('[DOC CAPTURE] useEffect fired, documentType:', documentType, 'captureMode:', captureMode)
    let mounted = true

    const initCamera = async () => {
      // Só inicializa câmera se estiver no modo camera
      if (captureMode !== 'camera') {
        return
      }

      try {
        console.log('[DOC CAPTURE] ===== CAMERA INIT =====')
        console.log('[DOC CAPTURE] User Agent:', navigator.userAgent)
        console.log('[DOC CAPTURE] Requested facingMode:', facingMode)

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false
        })

        if (!mounted) {
          stream.getTracks().forEach(track => track.stop())
          return
        }

        streamRef.current = stream

        if (videoRef.current) {
          videoRef.current.srcObject = stream

          // Log actual camera settings and determine if should mirror
          const videoTrack = stream.getVideoTracks()[0]
          if (videoTrack) {
            const settings = videoTrack.getSettings()
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

            // Mirror logic: ALWAYS mirror front camera (user), NEVER mirror back camera (environment)
            // facingMode: 'user' = front camera (mirror), 'environment' = back camera (no mirror)
            // If undefined (like on Mac), check if we requested 'user' or 'environment'
            let isFrontCamera = settings.facingMode === 'user'
            if (settings.facingMode === undefined) {
              // Fallback: check what we requested
              isFrontCamera = facingMode === 'user'
            }

            const mirror = isFrontCamera

            console.log('[DOC CAPTURE] Actual facingMode:', settings.facingMode)
            console.log('[DOC CAPTURE] Requested facingMode:', facingMode)
            console.log('[DOC CAPTURE] Camera label:', videoTrack.label)
            console.log('[DOC CAPTURE] Is Mobile:', isMobile)
            console.log('[DOC CAPTURE] Is Front Camera:', isFrontCamera)
            console.log('[DOC CAPTURE] Should Mirror:', mirror)
            console.log('[DOC CAPTURE] All settings:', settings)

            setShouldMirror(mirror)
          }
        }

        setIsReady(true)
        setInstruction(`Posicione a ${documentType} dentro da moldura`)
        console.log('[DOC CAPTURE] Camera ready')
        console.log('[DOC CAPTURE] =========================')
      } catch (err) {
        console.error('[DOC CAPTURE] Camera error:', err)
        setCameraError(err.message)
      }
    }

    initCamera()

    return () => {
      mounted = false
      console.log('[DOC CAPTURE] Cleanup - stopping camera for', documentType)

      // Stop all tracks
      if (streamRef.current) {
        console.log('[DOC CAPTURE] Stopping stream tracks:', streamRef.current.getTracks().length)
        streamRef.current.getTracks().forEach(track => {
          console.log('[DOC CAPTURE] Stopping track:', track.kind, track.label)
          track.stop()
        })
        streamRef.current = null
      }

      // Clear video source
      if (videoRef.current) {
        console.log('[DOC CAPTURE] Clearing video srcObject')
        videoRef.current.srcObject = null
      }
    }
  }, [documentType, facingMode, captureMode])

  const captureImage = (videoElement) => {
    if (!videoElement) return null

    try {
      const canvas = document.createElement('canvas')
      canvas.width = videoElement.videoWidth
      canvas.height = videoElement.videoHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(videoElement, 0, 0)
      return canvas
    } catch (error) {
      console.error('[SCANNER] Capture error:', error)
      return null
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (!file) return

    console.log('[DOC CAPTURE] File selected:', file.name, file.type, file.size)

    // Validar tipo de arquivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      setInstruction('❌ Formato inválido. Use JPEG, PNG, WebP ou PDF.')
      setHasError(true)
      return
    }

    // Validar tamanho (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setInstruction('❌ Arquivo muito grande. Máximo: 10MB.')
      setHasError(true)
      return
    }

    // Criar blob e preview
    const blob = new Blob([file], { type: file.type })
    const fileUrl = URL.createObjectURL(blob)
    const isPdf = file.type === 'application/pdf'

    setCapturedImage({ blob, url: fileUrl, isPdf, fileName: file.name })
    setShowPreview(true)
    setHasError(false)
    setInstruction('Confirme o arquivo selecionado')
    console.log('[DOC CAPTURE] File ready for upload')
  }

  const handleCapture = () => {
    console.log('[DOC CAPTURE] Capture button clicked')
    const canvas = captureImage(videoRef.current)

    if (!canvas) {
      setInstruction('Erro ao capturar. Tente novamente.')
      return
    }

    // Convert canvas to blob
    canvas.toBlob(blob => {
      console.log('[DOC CAPTURE] Image captured, size:', blob.size)
      const imageUrl = URL.createObjectURL(blob)
      setCapturedImage({ blob, url: imageUrl })
      setShowPreview(true)
      setInstruction('Confirme a imagem capturada')
    }, 'image/jpeg', 0.95)
  }

  const handleConfirm = async () => {
    console.log('[DOC CAPTURE] Confirm clicked')
    if (capturedImage) {
      setIsProcessing(true)
      setInstruction('Processando documento... Aguarde.')

      try {
        await onCapture(capturedImage.blob, documentType)
        // Se chegou aqui, deu certo - o componente vai desmontar
      } catch (error) {
        console.error('[DOC CAPTURE] Error during processing:', error)
        setIsProcessing(false)
        setHasError(true)

        // Mostrar erro específico do OCR
        const errorMessage = error.message || 'Erro ao processar documento'
        setInstruction(`❌ ${errorMessage}`)
        // Não limpa automaticamente - usuário precisa clicar em "Tirar Novamente"
      }
    }
  }

  const handleRetry = async () => {
    console.log('[DOC CAPTURE] Retry clicked')
    if (capturedImage?.url) {
      URL.revokeObjectURL(capturedImage.url)
    }
    setCapturedImage(null)
    setShowPreview(false)
    setHasError(false)
    setInstruction('Aguarde...')

    // Se estiver no modo câmera, reiniciar câmera
    if (captureMode === 'camera') {
      setIsReady(false)
      try {
        console.log('[DOC CAPTURE] Restarting camera...')
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false
        })

        streamRef.current = stream

        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }

        setIsReady(true)
        setInstruction(`Posicione a ${documentType} dentro da moldura`)
        console.log('[DOC CAPTURE] Camera restarted')
      } catch (err) {
        console.error('[DOC CAPTURE] Failed to restart camera:', err)
        setCameraError(err.message)
      }
    } else {
      // Modo upload: limpar input de arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setInstruction('Clique ou arraste uma foto do documento')
    }
  }

  const switchToCamera = async () => {
    console.log('[DOC CAPTURE] Switching to camera mode')
    setCaptureMode('camera')
    setHasError(false)
    setInstruction('Aguarde...')
  }

  const switchToUpload = () => {
    console.log('[DOC CAPTURE] Switching to upload mode')
    // Parar câmera se estiver rodando
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCaptureMode('upload')
    setIsReady(false)
    setHasError(false)
    setInstruction('Clique ou arraste uma foto do documento')
  }

  const toggleCamera = async () => {
    console.log('[DOC CAPTURE] Toggling camera')

    // Para a câmera atual
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
    }

    // Inverte o facingMode
    const newFacingMode = facingMode === 'environment' ? 'user' : 'environment'
    setFacingMode(newFacingMode)
  }

  if (cameraError) {
    return (
      <div className="document-capture-error">
        <p>❌ Erro ao acessar câmera: {cameraError}</p>
        <p>Permita o acesso à câmera e recarregue a página.</p>
      </div>
    )
  }

  // Determinar qual lado do documento baseado no documentType
  const isFrontSide = documentType?.includes('Frente')
  const docTypeName = documentType?.replace('Frente da ', '').replace('Verso da ', '') || 'CNH'

  return (
    <div className="document-capture-container">
      <div className="document-capture-card">
        <div className="document-header">
          <div className="document-header-badge">
            <div className="badge-dot"></div>
            VERIFICAÇÃO DE IDENTIDADE
          </div>
          <h2>Confirme seu documento</h2>
          <p className="document-subtitle">Processo seguro com criptografia de ponta a ponta</p>
        </div>

        {/* Barra de progresso */}
        <div className="progress-steps">
          <div className="progress-step active">
            <div className="step-circle">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
              </svg>
            </div>
            <div className="step-label">DADOS</div>
          </div>

          <div className="progress-line active"></div>

          <div className="progress-step current">
            <div className="step-circle">2</div>
            <div className="step-label">DOCUMENTO</div>
          </div>

          <div className="progress-line"></div>

          <div className="progress-step">
            <div className="step-circle">3</div>
            <div className="step-label">SELFIE</div>
          </div>

          <div className="progress-line"></div>

          <div className="progress-step">
            <div className="step-circle">4</div>
            <div className="step-label">PRONTO</div>
          </div>
        </div>

        {/* Miniaturas do documento */}
        <div className="doc-miniatures-container">
          <DocumentMiniature
            type={docTypeName}
            side="Frente"
            isActive={isFrontSide}
          />
          <DocumentMiniature
            type={docTypeName}
            side="Verso"
            isActive={!isFrontSide}
          />
        </div>

        {!showPreview ? (
          captureMode === 'upload' ? (
            <div className="upload-container">
              <div
                className="file-drop-zone"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <h3>Clique ou arraste um arquivo</h3>
                <p>JPEG, PNG, WebP ou PDF até 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button onClick={switchToCamera} className="btn-switch-mode">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Ou tirar foto com a câmera
              </button>
            </div>
          ) : (
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`video-feed ${shouldMirror ? 'mirror' : ''}`}
                onLoadedMetadata={() => console.log('[DOC CAPTURE] Video loaded, className:', `video-feed ${shouldMirror ? 'mirror' : ''}`, 'shouldMirror:', shouldMirror)}
              />

              <div className="document-guide">
                <div className="guide-frame">
                  <div className="corner corner-tl"></div>
                  <div className="corner corner-tr"></div>
                  <div className="corner corner-bl"></div>
                  <div className="corner corner-br"></div>
                </div>
              </div>

              {/* Botão para inverter câmera */}
              <button
                onClick={toggleCamera}
                className="btn-flip-camera"
                title="Trocar câmera"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"/>
                  <path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>

              {/* Botão para voltar ao upload */}
              <button onClick={switchToUpload} className="btn-switch-to-upload">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                Enviar arquivo
              </button>
            </div>
          )
        ) : (
          <div className="preview-container">
            {capturedImage?.isPdf ? (
              <div className="pdf-preview">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <p className="pdf-filename">{capturedImage?.fileName || 'documento.pdf'}</p>
                <p className="pdf-label">Arquivo PDF selecionado</p>
              </div>
            ) : (
              <img src={capturedImage?.url} alt="Documento capturado" className="preview-image" />
            )}
          </div>
        )}

        <div className="controls">
          {hasError && (
            <div className="instruction error">
              <span className="error-text">{instruction.replace('❌ ', '')}</span>
            </div>
          )}

          {!showPreview ? (
            captureMode === 'camera' && (
              <button
                onClick={handleCapture}
                className="btn-capture"
                disabled={!isReady}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Capturar Foto
              </button>
            )
          ) : (
            <div className="preview-buttons">
              <button
                onClick={handleRetry}
                className="btn-retry"
                disabled={isProcessing}
              >
                {captureMode === 'camera' ? 'Tirar Novamente' : 'Escolher Outra'}
              </button>
              <button
                onClick={handleConfirm}
                className="btn-confirm"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processando...' : 'Usar foto'}
              </button>
            </div>
          )}

          {onSkip && !showPreview && (
            <button onClick={onSkip} className="btn-skip">
              Pular Esta Etapa
            </button>
          )}
        </div>

        <div className="document-footer">
          <div className="footer-security">
            <svg className="footer-security-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Seus dados são protegidos e criptografados
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentCapture
